/** The price change the trade screen shows beside the price: over the chart's time frame, else 24 hours. */
import type { Timeframe } from '@platform/shared';
import { useChartCandle } from '../stores/chart';
import { useQuote, useTicker } from '../stores/market';

export const TF_LABEL: Record<Timeframe, string> = { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' };

export interface PriceMove {
  /** % change */
  pct: number;
  /** change in price */
  abs: number;
  /** "15m", "1H", "24h": the window it covers */
  label: string;
  /** what the label means, for a tooltip */
  title: string;
}

/**
 * While a chart shows the symbol: the move from the open of the candle it is drawing to the live price,
 * so picking 1H or 1D on the chart changes the figure. Without a chart: the ticker's 24-hour change.
 */
export function usePriceMove(symbol: string): PriceMove | null {
  const candle = useChartCandle(symbol);
  const quote = useQuote(symbol);
  const ticker = useTicker(symbol);
  const mark = quote ? Number(quote.mark) : null;
  if (candle && mark !== null && candle.o > 0) {
    const abs = mark - candle.o;
    const label = TF_LABEL[candle.tf];
    return { pct: (abs / candle.o) * 100, abs, label, title: `Change since this ${label} candle opened` };
  }
  if (ticker && ticker.chg24 !== null) return { pct: Number(ticker.chg24), abs: Number(ticker.chgAbs24 ?? 0), label: '24h', title: 'Change over 24 hours' };
  return null;
}
